import React, { useMemo } from "react";
import { classNames } from "primereact/utils";
import styles from "./GroupClassConfirmationStep.module.scss";
import {
  IGroupClass,
  IGroupClassInstructor,
  IGroupClassFamilyMember,
} from "../../models";
import { useAuthentication } from "../../../../../common/infrastructure/hooks/useAuthentication";
import { useAppContext } from "../../../../../AppContext";

interface IGroupClassConfirmationStepProps {
  className?: string;
  selectedClass?: IGroupClass | null;
  selectedInstructor?: IGroupClassInstructor | null;
  selectedDate?: Date | null;
  selectedTime?: string | null;
  durationMinutes?: number | null;
  actualInstructorName?: string | null;
  actualInstructorId?: number | null;
  selectedFamilyMember?: IGroupClassFamilyMember | null;
  isLoading?: boolean;
  isWaitlistBooking?: boolean;
  requirePrepayment?: boolean;
  prepaymentLink?: string | null;
  prepaymentProvider?: number;
}

export const GroupClassConfirmationStep = ({
  className,
  selectedClass,
  selectedInstructor,
  selectedDate,
  selectedTime,
  durationMinutes,
  actualInstructorName,
  actualInstructorId,
  selectedFamilyMember,
  isLoading = false,
  isWaitlistBooking = false,
  requirePrepayment = false,
  prepaymentLink,
  prepaymentProvider,
}: IGroupClassConfirmationStepProps) => {
  const { isAuthenticated } = useAuthentication();
  const { selectedStudio, currentUser } = useAppContext();

  // Calculate booking details
  const bookingDetails = useMemo(() => {
    if (
      !selectedClass ||
      !selectedInstructor ||
      !selectedDate ||
      !selectedTime
    ) {
      return null;
    }

    const formatDate = (date: Date): string => {
      const weekdays = [
        "неделя",
        "понеделник",
        "вторник",
        "сряда",
        "четвъртък",
        "петък",
        "събота",
      ];
      const months = [
        "януари",
        "февруари",
        "март",
        "април",
        "май",
        "юни",
        "юли",
        "август",
        "септември",
        "октомври",
        "ноември",
        "декември",
      ];
      const weekday = weekdays[date.getDay()];
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${day} ${month} ${year}`;
    };

    // Use actual instructor name from the selected slot when provided (for "any instructor" case).
    // An empty actual name means the booked class has no instructor, so it stays empty and is hidden.
    const instructorDisplayName =
      actualInstructorName !== undefined && actualInstructorName !== null
        ? actualInstructorName
        : selectedInstructor.id === -1
          ? "Всеки инструктор"
          : selectedInstructor.name;

    // Calculate time range (start - end)
    const formatTimeRange = (
      startTime: string,
      durationMins?: number | null,
    ): string => {
      if (!durationMins) return startTime;

      const [hours, minutes] = startTime.split(":").map(Number);
      const startDate = new Date();
      startDate.setHours(hours, minutes, 0, 0);

      const endDate = new Date(startDate);
      endDate.setMinutes(endDate.getMinutes() + durationMins);

      const endHours = endDate.getHours().toString().padStart(2, "0");
      const endMinutes = endDate.getMinutes().toString().padStart(2, "0");

      return `${startTime}-${endHours}:${endMinutes}`;
    };

    return {
      className: selectedClass.className,
      instructorName: instructorDisplayName,
      date: formatDate(selectedDate),
      time: formatTimeRange(selectedTime, durationMinutes),
      studio: selectedStudio,
      customer: currentUser,
    };
  }, [
    selectedClass,
    selectedInstructor,
    selectedDate,
    selectedTime,
    durationMinutes,
    actualInstructorName,
    selectedStudio,
    currentUser,
  ]);

  if (isLoading) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.header}>
          <h2 className={styles.title}>Детайли за резервацията</h2>
          <p className={styles.subtitle}>
            Моля, прегледайте детайлите на резервацията
          </p>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>Подготвяне на резервацията...</p>
        </div>
      </div>
    );
  }

  if (!bookingDetails) {
    return <></>;
  }

  return (
    <div className={classNames(styles.container, className)}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Потвърдете резервацията</h2>
        <p className={styles.subtitle}>
          Моля, прегледайте детайлите преди потвърждаване
        </p>
      </div>

      {/* Waitlist Warning */}
      {isWaitlistBooking && (
        <div className={styles.waitlistWarning}>
          <div className={styles.waitlistWarningIcon}>⏳</div>
          <div className={styles.waitlistWarningContent}>
            <h4 className={styles.waitlistWarningTitle}>Списък на чакащите</h4>
            <p className={styles.waitlistWarningText}>
              Няма свободни места за този час. Записвате се в списъка на
              чакащите. Ще бъдете уведомени, ако се освободи място.
            </p>
          </div>
        </div>
      )}

      {/* Prepayment Warning */}
      {requirePrepayment && !isWaitlistBooking && (
        <div className={styles.prepaymentWarning}>
          <div className={styles.prepaymentWarningIcon}>💳</div>
          <div className={styles.prepaymentWarningContent}>
            <h4 className={styles.prepaymentWarningTitle}>
              Изисква се предплащане
            </h4>
            <p className={styles.prepaymentWarningText}>
              Вашата резервация <strong>ще бъде потвърдена</strong> след
              плащане.
            </p>
            {prepaymentProvider === 2 ? (
              <p className={styles.prepaymentWarningText}>
                Ще бъдете пренасочени към Stripe за плащане веднага след потвърждаване.
              </p>
            ) : (
              <p className={styles.prepaymentWarningText}>
                След потвърждаване на резервацията, ще получите имейл с линк за
                плащане и инструкции.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Class Details */}
      <div className={styles.detailsCard}>
        <div className={styles.detailsHeader}>
          <div className={styles.classIcon}>🏋️</div>
          <div>
            <h3 className={styles.className}>{bookingDetails.className}</h3>
            <p className={styles.categoryName}>
              {selectedClass?.categoryName || "Други"}
            </p>
          </div>
        </div>

        <div className={styles.detailsList}>
          {bookingDetails.instructorName && (
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Инструктор:</span>
              <span className={styles.detailValue}>
                {bookingDetails.instructorName}
              </span>
            </div>
          )}
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Дата:</span>
            <span className={styles.detailValue}>{bookingDetails.date}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Час:</span>
            <span className={styles.detailValue}>{bookingDetails.time}</span>
          </div>
          {bookingDetails.studio && (
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Обект:</span>
              <span className={styles.detailValue}>
                {bookingDetails.studio.name}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Customer Information */}
      {isAuthenticated && currentUser ? (
        <div className={styles.customerSection}>
          <h3 className={styles.sectionTitle}>Информация за клиента</h3>
          <div className={styles.customerInfo}>
            <div className={styles.customerDetail}>
              <span className={styles.customerLabel}>Име:</span>
              <span className={styles.customerValue}>
                {selectedFamilyMember
                  ? selectedFamilyMember.name
                  : `${currentUser.firstName} ${currentUser.lastName}`}
              </span>
            </div>
            {(selectedFamilyMember
              ? selectedFamilyMember.email
              : (currentUser as any)?.email) && (
              <div className={styles.customerDetail}>
                <span className={styles.customerLabel}>Имейл:</span>
                <span className={styles.customerValue}>
                  {selectedFamilyMember
                    ? selectedFamilyMember.email
                    : (currentUser as any).email}
                </span>
              </div>
            )}
            {(selectedFamilyMember
              ? selectedFamilyMember.phoneNumber
              : currentUser?.phoneNumber) && (
              <div className={styles.customerDetail}>
                <span className={styles.customerLabel}>Телефон:</span>
                <span className={styles.customerValue}>
                  {selectedFamilyMember
                    ? selectedFamilyMember.phoneNumber
                    : currentUser.phoneNumber}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.authPrompt}>
          <div className={styles.authIcon}>👤</div>
          <h3 className={styles.authTitle}>Влезте в профила си</h3>
          <p className={styles.authText}>
            За да завършите резервацията, е необходимо да влезете в профила си
          </p>
        </div>
      )}

      {/* Important Notes */}
      <div className={styles.notesSection}>
        <h3 className={styles.sectionTitle}>Важна информация</h3>
        <div className={styles.notesList}>
          <div className={styles.noteItem}>
            <span className={styles.noteIcon}>📅</span>
            <span className={styles.noteText}>
              Моля, пристигнете поне 5 минути преди часа на класа
            </span>
          </div>
          <div className={styles.noteItem}>
            <span className={styles.noteIcon}>📱</span>
            <span className={styles.noteText}>
              Ще получите Email потвърждение след завършване на резервацията
            </span>
          </div>
          <div className={styles.noteItem}>
            <span className={styles.noteIcon}>⚡️</span>
            <span className={styles.noteText}>
              Можете да управлявате резервациите си в секция "Моите резервации"
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
